import {SequenceComponentHelper} from "../extensions/SequenceComponentHelper";
import {NodeSearchControl} from "../controls/NodeSearchControl";
import {SequenceComponent, StartComponent, EndComponent} from "../components";
import {StartComponentHelper} from "../extensions/StartComponentHelper";
import {
    NodeFileSearchControl,
    LabelNumberTextControl,
    LabelTextAreaControl
} from "../controls";
import {EndComponentHelper} from "../extensions/EndComponentHelper";

export function validateNodes(editor) {
    if (!editor || !editor.nodes || editor.nodes.length === 0) return [];

    const errors = [];

    for (let i = 0; i < editor.nodes.length; i++) {
        const node = editor.nodes[i];
        errors.push(...validateNodeComponents(node));
    }

    errors.push(...validateNodeConnections(editor.nodes));
    errors.push(...validateStartNode(editor.nodes));
    errors.push(...validateEndNode(editor.nodes));

    return errors;
}

function validateNodeComponents(node) {
    const errors = [];

    if (node.videoId != null) {
        const videoFileKey = SequenceComponentHelper.getVideoFileKey(node.videoId);
        if (node.data[videoFileKey] === undefined)
            throw new Error("Could not found VideoFile component data.");
        if (node.data[videoFileKey] === NodeFileSearchControl.DEFAULT_VALUE)
            errors.push(`Video file is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
    }

    if (node.defaultVoiceAnswerId !== undefined) {
        const nodeSearchKey = SequenceComponentHelper.getDefaultVoiceAnswerNodeSearchKey(node.defaultVoiceAnswerId);
        if (node.data[nodeSearchKey] === undefined) {
            throw new Error("Could not found NodeSearch component data.")
        }
        if (node.data[nodeSearchKey] === NodeSearchControl.DEFAULT_VALUE) {
            if (node.customName) {
                errors.push("Default voice answer node is not set - in Sequence of name: " + node.customName);
            } else {
                errors.push("Default voice answer node is not set - in Sequence of id: " + node.id);
            }
        }
    }

    const hasAnswer = SequenceComponentHelper.hasAnswer(node);

    if (node.avatarId != null && node.audioId == null)
        errors.push(`Avatar requires Audio - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);

    if (node.avatarId == null && node.audioId != null && (node.nestedComponents?.textQuestion.isAdded || hasAnswer))
        errors.push(`Audio cannot be combined with Text Question or answers without Avatar - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);

    if (node.imageId != null) {
        const imageFileKey = SequenceComponentHelper.getImageFileKey(node.imageId);
        const durationKey = SequenceComponentHelper.getImageDurationKey(node.imageId);
        if (node.data[imageFileKey] === undefined)
            throw new Error("Could not found ImageFile component data.");
        if (node.data[imageFileKey] === NodeFileSearchControl.DEFAULT_VALUE)
            errors.push(`Image file is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
        if (node.audioId == null && !hasAnswer && node.data[durationKey] === LabelNumberTextControl.DEFAULT_VALUE)
            errors.push(`Image duration is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
    }

    if (node.textSlideId != null) {
        const textKey = SequenceComponentHelper.getTextSlideTextKey(node.textSlideId);
        const durationKey = SequenceComponentHelper.getTextSlideDurationKey(node.textSlideId);
        if (node.data[textKey] === undefined)
            throw new Error("Could not found TextSlide's text component data.");
        if (node.data[textKey] === LabelTextAreaControl.DEFAULT_VALUE)
            errors.push(`TextSlide's text is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
        if (node.audioId == null && node.data[durationKey] === LabelNumberTextControl.DEFAULT_VALUE)
            errors.push(`TextSlide's duration is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
    }

    if (node.audioId != null) {
        const audioFileKey = SequenceComponentHelper.getAudioFileKey(node.audioId);
        if (node.data[audioFileKey] === undefined)
            throw new Error("Could not found AudioFile component data.");
        if (node.data[audioFileKey] === NodeFileSearchControl.DEFAULT_VALUE)
            errors.push(`Audio file is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
        if (node.imageId == null && node.textSlideId == null && node.avatarId == null)
            errors.push(`Audio file has missing Text Slide, Image or Avatar - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
    }

    if (node.infoIds != null) {
        for (const infoId of node.infoIds) {
            const iconFileKey = SequenceComponentHelper.getInfoIconFileKey(infoId);
            const textKey = SequenceComponentHelper.getInfoTextKey(infoId);
            if (node.data[iconFileKey] === NodeFileSearchControl.DEFAULT_VALUE)
                errors.push(`Info's icon file is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
            if (node.data[textKey] === LabelTextAreaControl.DEFAULT_VALUE)
                errors.push(`Info's text is not set - in Sequence of name: '${node.customName}' and id: '${node.id}'.`);
        }
    }

    return errors;
}

function validateNodeConnections(nodes) {
    const errors = [];

    for (const node of nodes) {
        if (node.outputs) {
            node.outputs.forEach(o => {
                const outputKey = o.key.split('_');
                if (outputKey[1] === SequenceComponent.VIDEO_CHILD_COMPONENT_KEY && SequenceComponentHelper.hasAnswer(node)) {
                    return;
                }
                if (outputKey[1] === SequenceComponent.AVATAR_CHILD_COMPONENT_KEY && !SequenceComponentHelper.isAvatarOwningOutput(node)) {
                    return;
                }
                if (outputKey[1] === SequenceComponent.IMAGE_CHILD_COMPONENT_KEY && SequenceComponentHelper.hasAnswer(node)) {
                    return;
                }
                if (outputKey[1] === SequenceComponent.TEXT_SLIDE_CHILD_COMPONENT_KEY && SequenceComponentHelper.hasAnswer(node)) {
                    return;
                }

                if (o.connections.length === 0) {
                    const nodeName = node.customName ? node.customName : node.id;
                    errors.push(`Socket has missing connection (output) - in Sequence of ${node.customName ? 'name: ' + nodeName : 'id: ' + nodeName}`);
                }
            });
        }

        if (node.inputs) {
            node.inputs.forEach(i => {
                if (i.connections.length === 0) {
                    const nodeName = node.customName ? node.customName : node.id;
                    errors.push(`Socket has missing connection (input) - in Sequence of ${node.customName ? 'name: ' + nodeName : 'id: ' + nodeName}`);
                }
            });
        }
    }

    return errors;
}

function validateStartNode(nodes) {
    const errors = [];
    let startNodeCount = 0;
    let startNode;

    for (const node of nodes) {
        if (node.name === StartComponent.COMPONENT_NAME) {
            startNodeCount++;
            startNode = node;
        }
    }

    if (startNodeCount === 0) {
        errors.push(`Missing '${StartComponent.COMPONENT_NAME}' node!`);
    } else if (startNodeCount === 1) {
        let startNodeOutputConnections = startNode.outputs.get(StartComponentHelper.getStartNodeOutputKey(startNode.componentId)).connections;
        if (startNodeOutputConnections.length === 0) {
            errors.push(`'${StartComponent.COMPONENT_NAME}' node must be connected with '${SequenceComponent.COMPONENT_NAME}' node.`);
        }
    } else if (startNodeCount > 1) {
        errors.push(`Only one '${StartComponent.COMPONENT_NAME}' node is allowed!`);
    }

    return errors;
}

function validateEndNode(nodes) {
    const errors = [];
    let endNodeCount = 0;
    let endNode;

    for (const node of nodes) {
        if (node.name === EndComponent.COMPONENT_NAME) {
            endNodeCount++;
            endNode = node;
        }
    }

    if (endNodeCount === 0) {
        errors.push(`Missing '${EndComponent.COMPONENT_NAME}' node!`);
    } else if (endNodeCount === 1) {
        let endNodeInputConnections = endNode.inputs.get(EndComponentHelper.getEndNodeInputKey(endNode.componentId)).connections;
        if (endNodeInputConnections.length === 0) {
            errors.push(`'${EndComponent.COMPONENT_NAME}' node must be connected with '${SequenceComponent.COMPONENT_NAME}' node.`);
        }
    } else if (endNodeCount > 1) {
        errors.push(`Only one '${EndComponent.COMPONENT_NAME}' node is allowed!`);
    }

    return errors;
}